import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgNode } from '@/types/analytics.types';
import { scoreStyle, getInitials, fmtScore } from '@/utils/analytics';

interface OrgTreeNodeProps {
  node: OrgNode;
  depth?: number;
}

const TYPE_LABELS: Record<string, { labelKey: string; bg: string; color: string }> = {
  OU:     { labelKey: 'analytics.heatmap.unitOU',   bg: '#FFF7ED', color: '#C2410C' },
  BU:     { labelKey: 'analytics.ona.typeBu',       bg: '#EFF6FF', color: '#1D4ED8' },
  Dept:   { labelKey: 'analytics.ona.typeDept',     bg: '#FDF2F8', color: '#BE185D' },
  Person: { labelKey: 'analytics.ona.typePerson',   bg: '#F0FDF4', color: '#166534' },
};

function omissionColor(rate: number): string {
  if (rate >= 10) return 'var(--cp-danger-light)';
  if (rate >= 5) return '#f59e0b';
  return '#10b981';
}

export function OrgTreeNode({ node, depth = 0 }: OrgTreeNodeProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(depth === 0);

  const hasChildren = (node.children?.length ?? 0) > 0;
  const isPerson = node.t === 'Person';
  const avatarSize = isPerson ? 32 : 38;
  const style = scoreStyle(node.sc ?? 0);
  const typeMeta = TYPE_LABELS[node.t] ?? { labelKey: node.t, bg: '#f3f4f6', color: '#374151' };
  const evaluations = node.ev ?? 0;
  const peopleCount = node.p ?? 0;
  // `om` solo llega en las vistas que calculan tasa de omisión; sin dato no se dibuja la barra
  // en vez de mostrar un 0% que se leería como "esta unidad no omite nada".
  const omissionRate = node.om;

  return (
    <div className="ona2-node" style={{ marginLeft: depth * 16 }}>
      <div
        className={`ona2-row${hasChildren ? ' clickable' : ''}${isExpanded && hasChildren ? ' open' : ''}`}
        onClick={() => hasChildren && setIsExpanded((current) => !current)}
        role={hasChildren ? 'button' : undefined}
        aria-expanded={hasChildren ? isExpanded : undefined}
      >
        <div
          className="ona2-avatar"
          style={{
            width: avatarSize,
            height: avatarSize,
            background: style.bg,
            borderColor: style.ring,
            color: style.text,
            fontSize: avatarSize * 0.32,
          }}
        >
          {getInitials(node.n)}
        </div>

        <div className="ona2-info">
          <div className="ona2-name-row">
            <span className={`ona2-name${isPerson ? ' ona2-name-person' : ''}`}>{node.n}</span>
            <span className="ona2-type" style={{ background: typeMeta.bg, color: typeMeta.color }}>
              {t(typeMeta.labelKey)}
            </span>
            <span
              className="ona2-chip"
              style={{ background: style.bg, color: style.text, borderColor: style.ring }}
            >
              {(node.sc ?? 0) > 0 ? fmtScore(node.sc) : '—'}
            </span>
          </div>
          <div className="ona2-meta">
            <span>
              {evaluations > 0
                ? t('analytics.summary.evaluationsCount', { count: evaluations })
                : t('analytics.ona.noEvals')}
            </span>
            {peopleCount > 0 && !isPerson && (
              <span>· {t('analytics.ona.collaboratorsCount', { count: peopleCount })}</span>
            )}
          </div>
        </div>

        {omissionRate !== undefined && (
          <div className="ona2-ombar">
            <div className="ona2-ombar-track">
              <div
                className="ona2-ombar-fill"
                style={{ width: `${Math.min(omissionRate, 100)}%`, background: omissionColor(omissionRate) }}
              />
            </div>
            <span style={{ color: omissionColor(omissionRate) }}>{omissionRate}%</span>
          </div>
        )}

        {hasChildren && <span className={`ona2-chevron${isExpanded ? ' open' : ''}`}>›</span>}
      </div>

      {isExpanded && hasChildren && (
        <div className="ona2-children">
          {node.children!.map((child) => (
            <OrgTreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
